"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FuelCategoryType, fuelCategories } from "@/types/fuelCategory.types";

interface NoCategoryFuelSelectProps {
  selectedCategory: FuelCategoryType;
  onSelectedCategory: (category: FuelCategoryType) => void;
}

export default function NoCategoryFuelSelect({ selectedCategory, onSelectedCategory }: NoCategoryFuelSelectProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Memoized function to compute dropdown width
  const computeDropdownWidth = useCallback(() => {
    if (!wrapperRef.current) return 0;

    const style = getComputedStyle(wrapperRef.current);
    const font = `${style.fontSize} ${style.fontFamily}`;

    const span = document.createElement("span");
    span.style.visibility = "hidden";
    span.style.position = "absolute";
    span.style.whiteSpace = "nowrap";
    span.style.font = font;
    document.body.appendChild(span);

    // Measure longest text (includes selected)
    const longestText = [...fuelCategories, selectedCategory].reduce((a, b) => (a.length > b.length ? a : b));
    span.textContent = longestText;

    const width = span.offsetWidth + 32; // padding/arrow buffer
    document.body.removeChild(span);

    return width;
  }, [selectedCategory]);

  const toggleOpen = () => {
    if (!open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const dropdownWidth = computeDropdownWidth();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: dropdownWidth });
    }
    setOpen(!open);
  };

  // Update width when selectedCategory changes
  useEffect(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const dropdownWidth = computeDropdownWidth();
      setMenuPos((prev) => ({ ...prev, width: dropdownWidth, left: rect.left }));
    }
  }, [selectedCategory, computeDropdownWidth]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="dropdown-menu">
      <div
        className="custom-select"
        ref={wrapperRef}
        style={{ width: 'auto', display: 'inline-block' }}
      >
        <div
          className="selected-option"
          onClick={toggleOpen}
          style={{ width: 'auto', whiteSpace: 'nowrap' }}
        >
          {selectedCategory}
          <span className={`arrow ${open ? "up" : "down"}`} />
        </div>
      </div>

      {open &&
        createPortal(
          <ul
            className="options-list"
            style={{
              position: "fixed",
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
              width: `${menuPos.width}px`,
              whiteSpace: "nowrap",
              zIndex: 9999,
            }}
          >
            {fuelCategories.map((cat) => (
              <li
                key={cat}
                role="option"
                aria-selected={cat === selectedCategory}
                className={cat === selectedCategory ? "active" : ""}
                onMouseDown={() => {
                  onSelectedCategory(cat as FuelCategoryType);
                  setOpen(false);
                }}
              >
                {cat}
              </li>
            ))}
          </ul>,
          document.body
        )
      }
    </div>
  );
}
